import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MonthSelector, {
  currentMonthKey,
  monthKeyToLabel,
} from "@/components/MonthSelector";
import {
  FileUp,
  FileX,
  FileText,
  CheckCircle2,
  Loader2,
  FileArchive,
  Zap,
} from "lucide-react";

export default function UploadPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [files, setFiles] = useState([]);
  const [descripcion, setDescripcion] = useState("");
  const [responsable, setResponsable] = useState(user?.default_responsable || "");
  const [mes, setMes] = useState(currentMonthKey());
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const acceptFiles = (list) => {
    const arr = Array.from(list).filter((f) => {
      const n = f.name.toLowerCase();
      return n.endsWith(".xml") || n.endsWith(".zip") || n.endsWith(".pdf");
    });
    setFiles((prev) => [...prev, ...arr]);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files) acceptFiles(e.dataTransfer.files);
  }, []);

  const onSelect = (e) => {
    if (e.target.files) acceptFiles(e.target.files);
  };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const submit = async () => {
    if (files.length === 0) {
      toast.error("Selecciona al menos un archivo XML, PDF o ZIP");
      return;
    }
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("descripcion", descripcion || (user?.razon_social || ""));
    fd.append("responsable", responsable || user?.default_responsable || user?.name || "");
    fd.append("mes", mes);

    setLoading(true);
    try {
      const { data } = await api.post("/batches/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Envío creado: ${data.total_docs} documento(s) · ${data.products_summary?.length || 0} producto(s)`);
      nav(`/batches/${data.id}`);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Carga masiva"
          title="Subir productos desde XML o PDF"
          description="Sube facturas electrónicas (33/34), guías de despacho (52) y notas de crédito (61) en formato XML del SII o PDF. El sistema extraerá automáticamente los productos y los distribuirá por comuna."
        />

        {/* Config fields */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-2">
              Mes del envío <span className="text-[#D95A40]">*</span>
            </label>
            <MonthSelector
              value={mes}
              onChange={setMes}
              testid="upload-mes-selector"
            />
            <div className="text-[10px] text-slate-500 mt-1.5">
              Todos los archivos se agruparán bajo{" "}
              <span className="text-[#0B1B3D] font-semibold">
                {monthKeyToLabel(mes)}
              </span>
              .
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-2">
              Descripción del envío
            </label>
            <input
              type="text"
              data-testid="upload-descripcion-input"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder={user?.razon_social || "Descripción SAP"}
              className="w-full px-3 py-2.5 bg-white border border-slate-300 focus:border-[#D95A40] focus:ring-2 focus:ring-[#D95A40]/20 focus:outline-none text-sm transition-colors"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600 block mb-2">
              Responsable
            </label>
            <input
              type="text"
              data-testid="upload-responsable-input"
              value={responsable}
              onChange={(e) => setResponsable(e.target.value)}
              placeholder={user?.default_responsable || user?.name || "Nombre del responsable"}
              className="w-full px-3 py-2.5 bg-white border border-slate-300 focus:border-[#D95A40] focus:ring-2 focus:ring-[#D95A40]/20 focus:outline-none text-sm transition-colors"
            />
          </div>
        </div>

        {/* Drop zone */}
        <div
          data-testid="upload-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer border-2 border-dashed p-10 md:p-16 text-center transition-colors ${
            dragActive
              ? "border-[#D95A40] bg-[#D95A40]/5"
              : "border-slate-300 bg-white hover:border-[#0B1B3D]"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".xml,.zip,.pdf"
            onChange={onSelect}
            className="hidden"
            data-testid="upload-file-input"
          />
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-[#0B1B3D] text-white flex items-center justify-center">
              <FileUp className="w-8 h-8" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display font-black text-2xl tracking-tighter text-[#0B1B3D]">
                Arrastra tus archivos aquí
              </div>
              <div className="text-sm text-slate-500 mt-2">
                o <span className="text-[#D95A40] font-bold">haz click para seleccionar</span>
              </div>
            </div>
            <div className="flex gap-6 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold mt-2">
              <span className="flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> .XML
              </span>
              <span className="flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> .PDF
              </span>
              <span className="flex items-center gap-1.5">
                <FileArchive className="w-3 h-3" /> .ZIP
              </span>
            </div>
          </div>
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="mt-6 bg-white border border-slate-200">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="font-display font-bold text-[#0B1B3D]">
                Archivos seleccionados{" "}
                <span className="text-slate-400 tabular-nums">
                  ({files.length})
                </span>
              </div>
              <button
                type="button"
                onClick={() => setFiles([])}
                data-testid="upload-clear-all"
                className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 hover:text-[#D95A40] transition-colors"
              >
                Limpiar
              </button>
            </div>
            <ul className="divide-y divide-slate-100 max-h-72 overflow-auto">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center justify-between px-5 py-3 text-sm"
                  data-testid={`upload-file-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {f.name.toLowerCase().endsWith(".zip") ? (
                      <FileArchive className="w-4 h-4 text-[#D95A40] shrink-0" />
                    ) : f.name.toLowerCase().endsWith(".pdf") ? (
                      <FileText className="w-4 h-4 text-red-500 shrink-0" />
                    ) : (
                      <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                    )}
                    <span className="truncate">{f.name}</span>
                    <span className="text-xs text-slate-400 tabular-nums shrink-0">
                      {(f.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  <button
                    onClick={() => removeFile(i)}
                    className="text-slate-400 hover:text-red-500 transition-colors ml-4"
                    data-testid={`upload-remove-${i}`}
                    type="button"
                  >
                    <FileX className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Submit */}
        <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-slate-200 pt-6">
          <div className="text-xs text-slate-600 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Los XML se procesan automáticamente y se distribuyen por comuna
            según el destino de entrega.
          </div>
          <button
            onClick={submit}
            disabled={loading || files.length === 0}
            data-testid="upload-submit-btn"
            className="flex items-center justify-center gap-2 px-8 py-3.5 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold uppercase tracking-[0.15em] text-sm transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Procesando...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" /> Procesar {files.length} archivo(s)
              </>
            )}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
